import type { FunctionComponent, ReactElement } from 'react';

import { defineSlotComponent, findAllForSlot, findFirstForSlot } from '../../layouts/slots';
import Flex from '../Flex';

import Card from './Card';
import type { Props as CardProps } from './Card';

import styles from './Card.module.less';

type Props = CardProps & {
  onHide: () => void;
};

type DismissibleCardComponent = FunctionComponent<Props> & {
  Header: FunctionComponent;
  SubHeader: FunctionComponent;
  Content: FunctionComponent;
};

/**
 * This is a helper wrapper for aligning the close button with the correct content. By wrapping the header and the
 * content in this component, we can make sure that it's always aligned with and spaced away from the child content.
 */
const CloseButtonWrapper: FunctionComponent<{ onHide: Props['onHide']; shouldUse?: boolean }> = ({
  children,
  shouldUse,
  onHide,
}) => {
  if (!shouldUse) {
    return children as ReactElement;
  }

  return (
    <Flex align="flex-start" justify="space-between" gap={12}>
      {children}
      <div>
        <button type="button" className={styles.closeButton} onClick={onHide} data-testid="dismissible-card-close-button">
          <span aria-hidden="true">×</span>
          <span className="sr-only">Close</span>
        </button>
      </div>
    </Flex>
  );
};

const DismissibleCard: DismissibleCardComponent = ({
  children,
  onHide,
  ...props
}) => {
  const header = findFirstForSlot('header', children);
  const subheader = findFirstForSlot('subheader', children);
  const content = findAllForSlot('content', children);

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading -- We want to pass this along to the main Card component
    <Card {...props}>
      {header && (
        <Card.Header>
          <CloseButtonWrapper onHide={onHide} shouldUse>
            {header}
          </CloseButtonWrapper>
        </Card.Header>
      )}
      {subheader && <Card.SubHeader>{subheader}</Card.SubHeader>}
      <Card.Content>
        <CloseButtonWrapper onHide={onHide} shouldUse={!header}>
          {content}
        </CloseButtonWrapper>
      </Card.Content>
    </Card>
  );
};

DismissibleCard.Header = defineSlotComponent('header', ({ children }) =>
  // eslint-disable-next-line react/jsx-no-useless-fragment, @stylistic/comma-dangle
  <>{children}</>
);
DismissibleCard.Header.displayName = 'Card.Header';

DismissibleCard.SubHeader = defineSlotComponent('subheader', ({ children }) =>
  // eslint-disable-next-line react/jsx-no-useless-fragment, @stylistic/comma-dangle
  <>{children}</>
);
DismissibleCard.SubHeader.displayName = 'Card.SubHeader';

DismissibleCard.Content = defineSlotComponent('content', ({ children }) =>
  // eslint-disable-next-line react/jsx-no-useless-fragment, @stylistic/comma-dangle
  <>{children}</>
);
DismissibleCard.Content.displayName = 'Card.Content';

export default DismissibleCard;
